import React, { useEffect, useState } from "react";
import { isNumberString } from "class-validator";
import { trim } from "lodash";
import { useFormStore } from "@modules/formx-react/useFormStore";
import { GetSubcontractorInvoice, SubcontractorInvoiceFragment } from "@graphql";
import { useDebounced, useStateRef } from "@hooks";
import locale from "@bonliva-crm/locale";
import { FormErrors } from "@modules/formx";
import { BookingSubcontractorInvoiceDialog } from "./BookingSubcontractorInvoiceDialog";
import { InvoiceSubcontractorDialogFormData } from "./utils";
import { useInvoiceProvider } from "../useInvoiceProvider";

type Options = {
  costCenterId: string;
  onSubmit: (value: InvoiceSubcontractorDialogFormData) => void;
};

const LIMIT_SIX_CHARACTER = 6;

export function useBookingSubcontractorInvoiceDialog(options: Options) {
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentInvoice, setCurrentInvoice] =
    useStateRef<SubcontractorInvoiceFragment | null>(null);
  const invoiceProvider = useInvoiceProvider(options.costCenterId);

  const { debounced: handleInvoiceNumber } = useDebounced((invoiceNumber: string) => {
    invoiceNumber = (invoiceNumber || "").replace(/[^0-9]/g, "");
    setCurrentInvoice(null);

    if (!invoiceNumber) {
      return;
    }

    setLoading(true);
    form.values.invoiceNumberShort = "";

    const truncateInvoiceNumber = invoiceNumber.substring(0, LIMIT_SIX_CHARACTER);

    GetSubcontractorInvoice.func(
      {
        number: truncateInvoiceNumber,
        provider: invoiceProvider
      },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        form.values.invoiceNumberShort = data.subcontractorInvoiceByNumber?.InvoiceNumber;
        setCurrentInvoice(data.subcontractorInvoiceByNumber);
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const form = useFormStore<InvoiceSubcontractorDialogFormData>({
    initialValues: {
      invoiceNumber: "",
      invoiceNumberShort: ""
    },
    onSubmit: () => {
      const value = trim(form.values.invoiceNumber);

      if (!value) {
        return Promise.resolve();
      }

      options.onSubmit(form.values);
      setShowDialog(false);
      return Promise.resolve();
    },
    onSetValue: ({ name, value }) => {
      if (name === "invoiceNumber") {
        handleInvoiceNumber(value);
      }
    },
    exposeAs: "bookingInvoiceDialog",
    validate: (form) => {
      if (!form) {
        return;
      }

      const { invoiceNumber } = form.values;
      const errors: FormErrors<InvoiceSubcontractorDialogFormData> = {};

      if (!invoiceNumber || !isNumberString(invoiceNumber)) {
        errors.invoiceNumber = locale.InvoiceNumber;
      }

      return errors;
    }
  });

  const isInvoiceFound = !form.values.invoiceNumber ? true : !!currentInvoice;
  const canSubmit = form.canSubmit && isInvoiceFound;

  const open = () => {
    setShowDialog(true);
    setCurrentInvoice(null);
    setLoading(false);
    form.reset(
      {
        invoiceNumber: "",
        invoiceNumberShort: ""
      },
      true
    );
  };

  useEffect(() => {
    form.reset({
      invoiceNumber: "",
      invoiceNumberShort: ""
    });
  }, [showDialog]);

  const dialog = showDialog && (
    <BookingSubcontractorInvoiceDialog
      form={form}
      canSubmit={canSubmit}
      loading={loading}
      currentInvoice={currentInvoice}
      isInvoiceFound={isInvoiceFound}
      onClose={() => setShowDialog(false)}
    />
  );

  return {
    form,
    canSubmit,
    isInvoiceFound,
    loading,
    currentInvoice,
    dialog,
    open
  };
}
